/**
 * Device fingerprinting utilities for client-side device identification
 */

/**
 * Generate a device fingerprint based on browser characteristics
 * @returns {Object} Device fingerprint data
 */
export const generateDeviceFingerprint = () => {
  try {
    // Get screen resolution
    const screenResolution = `${window.screen.width}x${window.screen.height}`
    
    // Get timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    // Get language
    const language = navigator.language || navigator.userLanguage
    
    // Get platform
    const platform = navigator.platform
    
    // Get color depth
    const colorDepth = window.screen.colorDepth
    
    // Get pixel ratio
    const pixelRatio = window.devicePixelRatio || 1
    
    // Get available screen size (excluding taskbar, etc.)
    const availableScreenSize = `${window.screen.availWidth}x${window.screen.availHeight}`
    
    // Get hardware concurrency (number of CPU cores)
    const hardwareConcurrency = navigator.hardwareConcurrency || 'unknown'
    
    // Get device memory (if available)
    const deviceMemory = navigator.deviceMemory || 'unknown'
    
    // Get touch support
    const touchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    
    return {
      screenResolution,
      timezone,
      language,
      platform,
      colorDepth,
      pixelRatio,
      availableScreenSize,
      hardwareConcurrency,
      deviceMemory,
      touchSupport,
      userAgent: navigator.userAgent
    }
  } catch (error) {
    console.error('Error generating device fingerprint:', error)
    return {
      screenResolution: 'unknown',
      timezone: 'unknown',
      language: 'unknown',
      userAgent: navigator.userAgent || 'unknown'
    }
  }
}

/**
 * Get a simplified device fingerprint for login
 * @returns {Object} Simplified device fingerprint
 */
export const getLoginFingerprint = () => {
  const fingerprint = generateDeviceFingerprint()
  
  return {
    screenResolution: fingerprint.screenResolution,
    timezone: fingerprint.timezone,
    language: fingerprint.language,
    userAgent: fingerprint.userAgent
  }
}

/**
 * Get device type based on screen size and user agent
 * @returns {string} Device type (desktop, tablet, mobile)
 */
export const getDeviceType = () => {
  const ua = navigator.userAgent.toLowerCase()
  const width = window.screen.width
  
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet'
  }
  
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile'
  }
  
  // Also check screen width as fallback
  if (width < 768) {
    return 'mobile'
  } else if (width >= 768 && width < 1024) {
    return 'tablet'
  }
  
  return 'desktop'
}

/**
 * Get a human-readable device name
 * @returns {string} Device name
 */
export const getDeviceName = () => {
  const ua = navigator.userAgent
  const deviceType = getDeviceType()
  
  // Try to extract browser name
  let browser = 'Unknown Browser'
  if (ua.indexOf('Firefox') > -1) {
    browser = 'Firefox'
  } else if (ua.indexOf('Chrome') > -1) {
    browser = 'Chrome'
  } else if (ua.indexOf('Safari') > -1) {
    browser = 'Safari'
  } else if (ua.indexOf('Edge') > -1) {
    browser = 'Edge'
  } else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident') > -1) {
    browser = 'Internet Explorer'
  }
  
  // Try to extract OS
  let os = 'Unknown OS'
  if (ua.indexOf('Windows') > -1) {
    os = 'Windows'
  } else if (ua.indexOf('Mac') > -1) {
    os = 'macOS'
  } else if (ua.indexOf('Linux') > -1) {
    os = 'Linux'
  } else if (ua.indexOf('Android') > -1) {
    os = 'Android'
  } else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) {
    os = 'iOS'
  }
  
  // Add emoji based on device type
  const emoji = deviceType === 'mobile' ? '📱' : deviceType === 'tablet' ? '📱' : '💻'
  
  return `${emoji} ${browser} on ${os}`
}

export default {
  generateDeviceFingerprint,
  getLoginFingerprint,
  getDeviceType,
  getDeviceName
}

